import { useMutation } from '@tanstack/react-query'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import { queryClient } from '@shared/providers/src/QueryClientProvider'
import API from '@shared/services/src/API'
import { BreakGlassType, QK } from '@shared/utils'

function useUnlock({ patientId, onSuccess }) {
  return useMutation({
    mutationFn: (data) => API.patients.id(patientId).breakGlass.unlock(data),
    onSuccess: onSuccess || (() => queryClient.refetchQueries({ queryKey: QK.patients.id(patientId).details })),
  })
}

export function useForm({ patientId, onSuccess, reasonRequired = false }) {
  const unlock = useUnlock({ patientId, onSuccess })

  return useFormik({
    validateOnBlur: false,
    validateOnChange: false,
    initialValues: { password: '', reason: '' },
    validationSchema: Yup.object().shape({
      password: Yup.string().trim().required('Password is required'),
      reason: reasonRequired ? Yup.string().required('Reason is required') : Yup.string(),
    }),
    onSubmit: ({ password, reason }, { setFieldError }) => {
      return unlock.mutateAsync({ password, reason: reason || undefined }).catch(() => setFieldError('password', 'No match was found'))
    },
  })
}

/**
 * HOC function to check if the user must break glass to access the patient.
 */
export function withBreakGlassCheck(Component) {
  return function BreakGlassCheck({ patient, ...props }) {
    if (isBreakGlassRequired(patient)) {
      return <Component {...props} patientId={patient?.id} reasonRequired={patient.breakGlass === BreakGlassType.RequiredWithReason} />
    }

    return props.children
  }
}

const isBreakGlassRequired = (patient) => [BreakGlassType.Required, BreakGlassType.RequiredWithReason].includes(patient?.breakGlass)
